<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type Panel, type Target } from '../lib/api'
  import { fmtRelative } from '../lib/utils'
  import { notify } from '../lib/toast'
  import Modal from '../components/Modal.svelte'

  let panels = $state<Panel[]>([])
  let targets = $state<Target[]>([])

  let addOpen = $state(false), editOpen = $state(false), tokenWizardOpen = $state(false)
  let editing = $state<Panel | null>(null)

  // Form state
  let f = $state({
    slug: '', name: '', type: 'remnawave' as 'remnawave', url: '', token: '',
    targetId: 0, artifact: 'remnawave-routing.txt', enabled: true,
    autoSync: true, syncIntervalSec: 900
  })

  // Token wizard
  let wz = $state({ url: '', username: '', password: '', result: '', loading: false })

  async function load() {
    [panels, targets] = await Promise.all([api.listPanels(), api.listTargets()])
  }
  onMount(load)

  const artifactsByType: Record<string, string[]> = {
    remnawave: ['remnawave-routing.txt', 'happ-base64.txt']
  }

  function resetForm() {
    f = { slug: '', name: '', type: 'remnawave', url: '', token: '', targetId: 0, artifact: 'remnawave-routing.txt', enabled: true, autoSync: true, syncIntervalSec: 900 }
  }

  function openAdd() {
    resetForm()
    addOpen = true
  }

  function openEdit(p: Panel) {
    editing = p
    f = {
      slug: p.slug, name: p.name, type: p.type, url: p.url, token: '',
      targetId: p.targetId, artifact: p.artifact, enabled: p.enabled,
      autoSync: p.autoSync ?? true, syncIntervalSec: p.syncIntervalSec || 900
    }
    editOpen = true
  }

  const intervalPresets = [
    { value: 60, label: '1 минута' },
    { value: 300, label: '5 минут' },
    { value: 900, label: '15 минут' },
    { value: 1800, label: '30 минут' },
    { value: 3600, label: '1 час' },
    { value: 21600, label: '6 часов' },
    { value: 86400, label: '24 часа' }
  ]
  function formatInterval(sec: number) {
    if (sec < 60) return `${sec}с`
    if (sec < 3600) return `${Math.round(sec / 60)} мин`
    if (sec < 86400) return `${Math.round(sec / 3600)} ч`
    return `${Math.round(sec / 86400)} дн`
  }

  async function handleSave() {
    if (!f.slug || !f.name || !f.url || !f.targetId) return notify('Заполните все поля', 'err')
    try {
      const body: any = { ...f }
      if (!body.token) delete body.token
      if (editing) await api.updatePanel(editing.id, body)
      else await api.createPanel(body)
      notify('Сохранено', 'ok')
      addOpen = false; editOpen = false; editing = null
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function handleDelete(p: Panel) {
    if (!confirm(`Удалить панель ${p.slug}?`)) return
    try { await api.deletePanel(p.id); notify('Удалено', 'ok'); await load() }
    catch (e: any) { notify(e.message, 'err') }
  }

  async function handleSync(p: Panel) {
    try {
      const r = await api.syncPanel(p.id)
      if (r.skipped) notify(`Без изменений (${r.reason})`, 'ok')
      else if (r.ok) notify(`Синхронизировано`, 'ok')
      else notify('Ошибка: ' + r.error, 'err')
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function handleTest(p: Panel) {
    try {
      const r = await api.testPanel(p.id)
      if (r.ok) notify(`✓ Соединение OK${r.profileTitle ? ' · ' + r.profileTitle : ''}`, 'ok')
      else notify('✗ ' + (r.error || 'failed'), 'err')
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function wizardCreate() {
    if (!wz.url || !wz.username || !wz.password) return notify('Заполните поля', 'err')
    wz.loading = true
    try {
      const r = await api.createRemnawaveToken({ url: wz.url, username: wz.username, password: wz.password })
      wz.result = r.token
      notify('API-токен создан', 'ok')
    } catch (e: any) { notify(e.message, 'err') }
    finally { wz.loading = false }
  }

  function useTokenFromWizard() {
    f.url = wz.url
    f.token = wz.result
    tokenWizardOpen = false
    if (!addOpen) addOpen = true
  }
</script>

<div class="card mb-4">
  <div class="flex justify-between items-center flex-wrap gap-2">
    <div>
      <h2 class="text-base font-semibold">Внешние панели</h2>
      <p class="text-xs text-muted mt-1">
        Автоматическая доставка артефактов в Remnawave (и другие) панели. Синхронизация каждые 15 минут + вручную.
      </p>
    </div>
    <div class="flex gap-2">
      <button class="btn btn-secondary" onclick={() => { wz = { url: '', username: '', password: '', result: '', loading: false }; tokenWizardOpen = true }}>Получить API-токен</button>
      <button class="btn btn-primary" onclick={openAdd}>+ Панель</button>
    </div>
  </div>
</div>

<div class="grid md:grid-cols-2 gap-4">
  {#each panels as p (p.id)}
    {@const target = targets.find(t => t.id === p.targetId)}
    <div class="card">
      <div class="flex justify-between items-start gap-2 mb-2 flex-wrap">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <h3 class="font-semibold truncate">{p.name}</h3>
            <span class="badge badge-blue">{p.type}</span>
            {#if p.enabled}<span class="badge badge-green">активна</span>{:else}<span class="badge badge-red">отключена</span>{/if}
          </div>
          <div class="text-xs text-muted mt-1 break-all">
            <code>{p.slug}</code> · <a href={p.url} target="_blank" class="text-blue-300">{p.url}</a>
          </div>
        </div>
        <div class="flex gap-1 flex-shrink-0">
          <button class="btn-icon" onclick={() => handleTest(p)} title="Проверить соединение">🔌</button>
          <button class="btn-icon" onclick={() => handleSync(p)} title="Синхронизировать сейчас">🔄</button>
          <button class="btn-icon" onclick={() => openEdit(p)} title="Редактировать">✎</button>
          <button class="btn-icon btn-icon-danger" onclick={() => handleDelete(p)} title="Удалить">🗑</button>
        </div>
      </div>

      <div class="mt-3 pt-3 border-t border-bg-border grid grid-cols-2 gap-2 text-xs">
        <div>
          <div class="text-muted">Target</div>
          <div>{target?.name || '—'}</div>
        </div>
        <div>
          <div class="text-muted">Artifact</div>
          <div><code class="text-[11px]">{p.artifact}</code></div>
        </div>
        <div>
          <div class="text-muted">API-токен</div>
          <div><code class="text-[11px]">{p.authDataMask || '—'}</code></div>
        </div>
        <div>
          <div class="text-muted">Синхронизаций</div>
          <div>{p.syncCount}</div>
        </div>
        <div class="col-span-2">
          <div class="text-muted">Автообновление</div>
          <div>
            {#if p.autoSync}
              <span class="text-emerald-300">✓ включено</span> — каждые {formatInterval(p.syncIntervalSec)}
            {:else}
              <span class="text-muted">✗ отключено — только ручная</span>
            {/if}
          </div>
        </div>
      </div>

      <div class="mt-3 pt-3 border-t border-bg-border">
        {#if p.lastSyncAt}
          {#if p.lastSyncOk}
            <div class="text-xs text-emerald-400">✓ Последняя синхронизация: {fmtRelative(p.lastSyncAt)}</div>
          {:else}
            <div class="text-xs text-red-400">✗ Ошибка: {fmtRelative(p.lastSyncAt)}</div>
            {#if p.lastSyncError}<div class="text-[11px] text-muted mt-1 font-mono">{p.lastSyncError}</div>{/if}
          {/if}
        {:else}
          <div class="text-xs text-muted">Ещё не синхронизировалась</div>
        {/if}
      </div>
    </div>
  {/each}
  {#if panels.length === 0}
    <div class="card col-span-full text-center text-muted py-8">Нет ни одной панели</div>
  {/if}
</div>

{#if addOpen || editOpen}
  <Modal title={editing ? 'Редактировать панель' : 'Новая панель'} onclose={() => { addOpen = false; editOpen = false; editing = null }} wide>
    <div class="space-y-3">
      <div class="grid grid-cols-2 gap-3">
        <div><label class="label">Slug</label><input class="input" bind:value={f.slug} placeholder="webitrix" disabled={!!editing}></div>
        <div><label class="label">Название</label><input class="input" bind:value={f.name} placeholder="Webitrix Remnawave"></div>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label">Тип</label>
          <select class="input" bind:value={f.type}>
            <option value="remnawave">Remnawave</option>
          </select>
        </div>
        <div><label class="label">URL</label><input class="input" bind:value={f.url} placeholder="https://cp.example.com"></div>
      </div>
      <div>
        <label class="label">API-токен</label>
        <input class="input font-mono text-xs" bind:value={f.token} placeholder={editing ? 'оставьте пустым если не менять' : 'eyJhbGc...'}>
        <p class="text-xs text-muted mt-1">
          Нужен API-токен от Remnawave (не admin пароль).
          <button class="text-blue-300 hover:underline" onclick={() => { wz.url = f.url; tokenWizardOpen = true }}>
            Получить через мастер →
          </button>
        </p>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label">Target</label>
          <select class="input" bind:value={f.targetId}>
            <option value={0}>—</option>
            {#each targets as t}<option value={t.id}>{t.name} ({t.type})</option>{/each}
          </select>
        </div>
        <div>
          <label class="label">Артефакт</label>
          <select class="input" bind:value={f.artifact}>
            {#each artifactsByType[f.type] || [] as a}<option value={a}>{a}</option>{/each}
          </select>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-3 pt-3 border-t border-bg-border">
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" class="checkbox" bind:checked={f.enabled}>
          <span class="text-sm">Активна</span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" class="checkbox" bind:checked={f.autoSync}>
          <span class="text-sm">Автоматическая синхронизация</span>
        </label>
        {#if f.autoSync}
          <div>
            <label class="label">Интервал синхронизации</label>
            <div class="flex gap-2 items-center">
              <select class="input w-auto" bind:value={f.syncIntervalSec}>
                {#each intervalPresets as p}<option value={p.value}>{p.label}</option>{/each}
                <option value={f.syncIntervalSec} hidden={intervalPresets.some(p => p.value === f.syncIntervalSec)}>Кастомный: {formatInterval(f.syncIntervalSec)}</option>
              </select>
              <input class="input w-24" type="number" min="60" step="60" bind:value={f.syncIntervalSec}>
              <span class="text-xs text-muted">секунд (мин. 60)</span>
            </div>
          </div>
        {/if}
      </div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => { addOpen = false; editOpen = false; editing = null }}>Отмена</button>
      <button class="btn btn-primary" onclick={handleSave}>Сохранить</button>
    {/snippet}
  </Modal>
{/if}

{#if tokenWizardOpen}
  <Modal title="Мастер создания API-токена" onclose={() => (tokenWizardOpen = false)} wide>
    <p class="text-xs text-muted mb-4">
      Вводим admin логин/пароль от панели — система создаст long-lived API-токен через <code>POST /api/tokens</code>.
      Логин/пароль никуда не сохраняется.
    </p>
    <div class="space-y-3">
      <div><label class="label">URL панели</label><input class="input" bind:value={wz.url} placeholder="https://cp.example.com"></div>
      <div class="grid grid-cols-2 gap-3">
        <div><label class="label">Admin username</label><input class="input" bind:value={wz.username}></div>
        <div><label class="label">Admin password</label><input class="input" type="password" bind:value={wz.password}></div>
      </div>
      {#if wz.result}
        <div class="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
          <div class="text-xs text-emerald-300 mb-2">✓ API-токен создан:</div>
          <code class="text-[11px] break-all block bg-bg-sunk p-2 rounded">{wz.result}</code>
          <button class="btn btn-primary mt-3" onclick={useTokenFromWizard}>Использовать в форме</button>
        </div>
      {/if}
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (tokenWizardOpen = false)}>Закрыть</button>
      <button class="btn btn-primary" disabled={wz.loading} onclick={wizardCreate}>
        {wz.loading ? 'Создаю...' : 'Создать токен'}
      </button>
    {/snippet}
  </Modal>
{/if}
